import React, { HTMLAttributes, useEffect, useRef, useState } from "react";
import { AriaListBoxProps, VisuallyHidden } from "react-aria";
import { Item, useListState } from "react-stately";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { Dots } from "./Dots";

import styles from "./ListCarousel.module.scss";

type CarouselProps = Omit<AriaListBoxProps<Record<string, never>>, "children"> &
    React.PropsWithChildren<{
        className?: string;
        style?: React.CSSProperties | undefined;
        wrapperAttrs?: HTMLAttributes<HTMLDivElement>;
        listItemAttrs?: HTMLAttributes<HTMLLIElement>;
        navigationProps?: {
            size?: "large" | "small";
            className?: string;
        };
        progressClassName?: string;
        dotsClassName?: string;
    }>;

interface CustomCss extends React.CSSProperties {
    "--scroll-progress-decimal": number;
    "--scroll-progress-percent": string;
}

/**
 * ListCarousel outputs a carousel as an unordered list, and relies heavily on
 * IntersectionObserver to update the markup based on what is visible on screen.
 *
 * The reason why this exists is that Carousel uses the tab aria role, which
 * doesn't work if the carousel requires more than a single slide (tab) to be
 * visible at the same time. (The underlying code uses `inert` for inactive
 * tabs, which causes problems when the inert thing is visible and you want to
 * click it).
 *
 * This markup heavily follows the example provided by WAI's carousel tutorial
 * https://www.w3.org/WAI/tutorials/carousels/, while incorporating
 * aria-roledescription, which did not exist at the time, as the author notes:
 * https://github.com/w3c/wai-tutorials/issues/594#issuecomment-530309064)
 *
 */
export function ListCarousel({
    autoFocus,
    wrapperAttrs,
    listItemAttrs,
    label,
    navigationProps = {
        size: "large",
    },
    progressClassName,
    dotsClassName,
    ...containerProps
}: CarouselProps) {
    /**
     * If you don't wrap in <Item>, an error is thrown:
     * `type.getCollectionNode is not a function`
     * This abstracts the problem away.
     */
    const itemChildren = React.Children.map(
        containerProps.children,
        (child, index) => (
            <Item
                textValue="unused"
                key={
                    child && typeof child == "object" && "key" in child
                        ? child.key
                        : `list-carousel-item-${index}`
                }
            >
                {child}
            </Item>
        ),
    );

    /**
     * The ratio of visibility that triggers the intersection observer.
     * This sets it in one spot and reads it in another, so this will keep
     * them linked.
     *
     * The value is somewhat arbitrary, and honestly it might need to be
     * configurable down the line. It was originally `1`, but that had problems
     * when the ratio was something like 0.9999956, and if the slide is a link,
     * a user might want to click that's 80% visible but would be unable to.
     */
    const threshold = 0.6;

    const state = useListState({
        selectionMode: "multiple",
        selectionBehavior: "replace",
        ...containerProps,
        children: itemChildren,
    });

    const wrapper = useRef<HTMLDivElement>(null);
    const list = useRef<HTMLUListElement>(null);

    const [prevScrollBehavior, setPrevScrollBehavior] = useState<
        "last" | "adjacent"
    >("adjacent");
    const [nextScrollBehavior, setNextScrollBehavior] = useState<
        "first" | "adjacent"
    >("adjacent");
    const [itemWidth, setItemWidth] = useState(10);
    const [allVisible, setAllVisible] = useState(true);
    const [scrollProgress, setScrollProgress] = useState<number>(0);

    /**
     * When clicking previous or next buttons, it should advance by one slide
     * forward or backwards; CSS scroll snapping plus smooth scrolling here
     * is making that look clean.
     *
     * But there are situations where we want to jump to the beginning or end;
     * this uses prevScrollBehavior and nextScrollBehavior, which is set by
     * the intersection observer.
     */
    const scroll = (left: number) => {
        if (!wrapper.current) return;
        const ref = wrapper.current;

        if (nextScrollBehavior === "first" && left > 0) {
            ref.scrollTo({ left: 0, behavior: "smooth" });
        } else if (prevScrollBehavior === "last" && left < 0) {
            ref.scrollTo({ left: ref.scrollWidth, behavior: "smooth" });
        } else {
            ref.scrollBy({ left, behavior: "smooth" });
        }
    };

    const jump = (left: number) => {
        if (!wrapper.current) return;
        const ref = wrapper.current;
        ref.scrollTo({ left, behavior: "smooth" });
    };

    useEffect(() => {
        if (!list.current) return;
        const elementsToWatch = list.current.querySelectorAll("li");

        /**
         * There are some oddities here because state.selectionManager.setSelectedKeys()
         * needed to have `state` passed into the effect to work properly, but doing
         * that reinitializes the observer. Was just looking for ways to cut down
         * rerenders overall.
         */
        const observer = new IntersectionObserver(
            (entries) => {
                /**
                 * A single entry usually means that an entry has updated, usually
                 * for isIntersecting to toggle. We don't need everything to change
                 * when that happens.
                 */
                const singleEntry = entries.length === 1;

                /** Anything that's visible should be "selected" */
                const visibleEntryIds = entries
                    .filter((entry) => entry.intersectionRatio > threshold)
                    .map((entry) => entry.target.id);
                /**
                 * Assumption: at least one slide will always be fully visible.
                 * Happy accident, I think?: the slide going offscreen doesn't
                 * lose focus until the next slide coming on is visible.
                 */
                if (visibleEntryIds.length > 0) {
                    state.selectionManager.setSelectedKeys(visibleEntryIds);
                }

                /**
                 * If the first item is visible, then clicking the previous
                 * button should take you to the end of the carousel
                 */
                const firstIsVisible =
                    entries.find(
                        (entry) =>
                            entry.target.id === state.collection.getFirstKey(),
                    )?.isIntersecting || false;
                if (!singleEntry) {
                    setPrevScrollBehavior(firstIsVisible ? "last" : "adjacent");
                }

                /**
                 * If the last item is visible, then clicking the next
                 * button should take you to the beginning of the carousel
                 */
                const lastIsVisible =
                    entries.find(
                        (entry) =>
                            entry.target.id === state.collection.getLastKey(),
                    )?.isIntersecting || false;
                if (!singleEntry) {
                    setNextScrollBehavior(lastIsVisible ? "first" : "adjacent");
                }
            },
            { threshold },
        );
        elementsToWatch.forEach((element) => observer.observe(element));

        return () => {
            elementsToWatch.forEach((element) => observer.unobserve(element));
        };
    }, [state]);

    useEffect(() => {
        const onResize = () => {
            /**
             * This shouldn't be necessary, but Safari on iOS doesn't pay attention to
             * scroll snapping when using scrollBy. This isn't perfect because it
             * assumes that every item is the same width and it doesn't account for margins,
             * but I figure it's passable.
             *
             * (And since it's here, itemWidth is now being used to `jump()`)
             */
            setItemWidth(
                list.current?.querySelector("li")?.getBoundingClientRect()
                    .width || 10,
            );

            /**
             * If there's no scrolling, the button nav is unnecessary
             */
            if (wrapper.current && list.current) {
                setAllVisible(
                    wrapper.current.scrollWidth <=
                        wrapper.current.getBoundingClientRect().width,
                );
            }
        };

        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (!progressClassName) return;
        const onScroll = () => {
            setScrollProgress(
                (wrapper.current!.scrollLeft + wrapper.current!.clientWidth) /
                    wrapper.current!.scrollWidth,
            );
        };

        onScroll();
        if (wrapper.current)
            wrapper.current.addEventListener("scroll", onScroll);

        return () => {
            if (wrapper.current) window.removeEventListener("scroll", onScroll);
        };
    }, []);

    const activeIndexes = [...state.selectionManager.selectedKeys].map(
        (key) => `${[...state.collection.getKeys()].indexOf(key) + 1}`,
    );

    const listFormat =
        typeof Intl.ListFormat !== "undefined"
            ? new Intl.ListFormat("en")
            : null;
    const prettyActiveIndexes: string = listFormat
        ? listFormat.format(activeIndexes)
        : activeIndexes.join(", ");

    return (
        <>
            <div
                className={concatClassNames([
                    styles.controls,
                    navigationProps.className,
                ])}
                style={{ display: allVisible ? "none" : undefined }}
            >
                <Clickable
                    className={concatClassNames([
                        styles.navArrow,
                        styles.previous,
                    ])}
                    aria-label={
                        prevScrollBehavior === "last"
                            ? t`Go to last item`
                            : t`Previous item`
                    }
                    onPress={() => scroll(-itemWidth)}
                >
                    <Svg name="arrow" />
                </Clickable>
                <Clickable
                    aria-label={
                        nextScrollBehavior === "first"
                            ? t`Go to first item`
                            : t`Next item`
                    }
                    onPress={() => scroll(itemWidth)}
                    className={styles.navArrow}
                >
                    <Svg name="arrow" />
                </Clickable>
            </div>
            <div
                {...wrapperAttrs}
                className={concatClassNames([
                    styles.wrapperHorizontalScroll,
                    wrapperAttrs?.className,
                ])}
                ref={wrapper}
            >
                <div aria-live="polite" aria-atomic="true">
                    {activeIndexes.length > 0 && (
                        <VisuallyHidden>
                            {t`Showing items ${prettyActiveIndexes} of ${state.collection.size}`}
                        </VisuallyHidden>
                    )}
                </div>

                <ul
                    {...containerProps}
                    className={concatClassNames([
                        styles.ulHorizontalScroll,
                        containerProps.className,
                    ])}
                    role="group"
                    aria-roledescription="carousel"
                    aria-label={typeof label === "string" ? label : ""}
                    ref={list}
                >
                    {[...state.collection].map((item) => (
                        <li
                            {...listItemAttrs}
                            className={concatClassNames([
                                styles.liHorizontalScroll,
                                listItemAttrs?.className,
                            ])}
                            inert={
                                !state.selectionManager.isSelected(item.key)
                                    ? true
                                    : undefined
                            }
                            id={item.key.toString()}
                            key={item.key}
                        >
                            {item.rendered}
                        </li>
                    ))}
                </ul>
            </div>
            {dotsClassName && (
                <Dots
                    activeIndexes={activeIndexes}
                    totalSize={state.collection.size}
                    className={dotsClassName}
                    onClick={(index) => jump(index * itemWidth)}
                />
            )}
            {/**
             * Progress bar. Will not show unless a class is passed in, to prevent
             * unnecessary scroll event firing. Hidden from aria because I don't
             * think it adds anything - we already have a live section that says
             * "Showing items 1 of 6" or whatever it is, so a progress percent
             * feels redundant.
             */}
            {progressClassName && (
                <div
                    className={progressClassName}
                    style={
                        {
                            "--scroll-progress-decimal": scrollProgress,
                            "--scroll-progress-percent": `${scrollProgress * 100}%`,
                        } as CustomCss
                    }
                    aria-hidden="true"
                />
            )}
        </>
    );
}
